import { useEffect, useRef, useState } from 'react';
import { X, Send } from 'lucide-react';
import { getFunnelSessionId } from '../lib/funnel';

// Survey reasons — single-select. Keep in sync with the spec.
const REASONS = [
  "I don't understand how ConnectMail works.",
  "I don't think there are enough professionals using it yet.",
  "Setting everything up looks like too much work.",
  "I don't see enough value yet.",
  "I'm just looking around.",
  "I'll come back later.",
  'Other',
];

const SHOWN_KEY = 'cm_exit_survey_shown';
const SUBMITTED_KEY = 'cm_exit_survey_submitted';

/**
 * Exit-intent survey modal.
 *
 * Triggers:
 *  1. Desktop "exit intent" — cursor leaves through the top edge of the viewport.
 *  2. Dwell timer — 45 seconds on page without converting.
 *  3. (When mounted on Register page) component unmount without form submission
 *     — see `triggerOnUnmount` prop.
 *
 * The modal is shown at most once per browser tab session (sessionStorage).
 * Once a visitor submits, we never show it again on this device (localStorage).
 */
export const ExitIntentSurvey = ({ triggerOnUnmount = false, submittedRef = null }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const alreadyShownRef = useRef(false);
  const dwellTimerRef = useRef(null);

  const trySchedule = (delayMs) => {
    if (alreadyShownRef.current) return;
    if (sessionStorage.getItem(SHOWN_KEY) === '1') return;
    if (localStorage.getItem(SUBMITTED_KEY) === '1') return;
    if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = setTimeout(() => fire(), delayMs);
  };

  const fire = () => {
    if (alreadyShownRef.current) return;
    if (sessionStorage.getItem(SHOWN_KEY) === '1') return;
    if (localStorage.getItem(SUBMITTED_KEY) === '1') return;
    alreadyShownRef.current = true;
    sessionStorage.setItem(SHOWN_KEY, '1');
    setOpen(true);
  };

  useEffect(() => {
    // 1. Exit-intent (desktop) — cursor moves above the viewport top edge.
    const onMouseOut = (e) => {
      if (!e.relatedTarget && (e.clientY ?? 9999) < 10) fire();
    };
    window.addEventListener('mouseout', onMouseOut);

    // 2. Dwell timer — 45s without conversion.
    trySchedule(45000);

    return () => {
      window.removeEventListener('mouseout', onMouseOut);
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Unmount trigger — used by RegisterPage. If the user leaves the
  // register flow without submitting (the page tells us via submittedRef),
  // schedule the survey before navigation completes.
  useEffect(() => {
    if (!triggerOnUnmount) return;
    return () => {
      const submittedFlag = submittedRef && submittedRef.current === true;
      if (!submittedFlag) fire();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerOnUnmount]);

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/feedback/exit-survey`;
      const payload = {
        reason,
        other_text: reason === 'Other' ? otherText : '',
        landing_url: typeof window !== 'undefined' ? window.location.href : '',
        session_id: getFunnelSessionId(),
      };
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
      localStorage.setItem(SUBMITTED_KEY, '1');
      setSubmitted(true);
    } catch (_) {
      // Even on failure we treat it as submitted to avoid annoying the user
      localStorage.setItem(SUBMITTED_KEY, '1');
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-3 sm:p-6"
      onClick={handleClose}
      data-testid="exit-survey-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Exit-intent survey"
        data-testid="exit-survey-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#1e3a5f] via-[#1f7ab3] to-emerald-600 px-5 py-4 rounded-t-2xl relative text-white">
          <button
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-7 h-7 rounded-md hover:bg-white/15 flex items-center justify-center"
            data-testid="exit-survey-close"
          >
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-lg font-bold font-['Outfit']" data-testid="exit-survey-title">
            {submitted ? 'Thanks for the feedback' : 'Before You Go...'}
          </h2>
          <p className="text-[12px] text-white/85 mt-1">
            {submitted
              ? "We've recorded your response. Stay tuned — we're improving fast."
              : "We're always improving ConnectMail."}
          </p>
        </div>

        {/* Body */}
        {submitted ? (
          <div className="px-5 py-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-slate-800" data-testid="exit-survey-thanks">
              Thank you! Your feedback helps us improve ConnectMail.
            </p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              data-testid="exit-survey-thanks-close"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm text-slate-600">
              Would you mind telling us what stopped you from creating an account today?
            </p>
            <div className="space-y-1.5" role="radiogroup" data-testid="exit-survey-options">
              {REASONS.map((r) => {
                const selected = reason === r;
                return (
                  <label
                    key={r}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      selected
                        ? 'border-emerald-500 bg-emerald-50/60'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`exit-survey-option-${r.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
                  >
                    <input
                      type="radio"
                      name="exit-survey-reason"
                      value={r}
                      checked={selected}
                      onChange={() => setReason(r)}
                      className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-[13px] text-slate-700 leading-snug">{r}</span>
                  </label>
                );
              })}
            </div>

            {reason === 'Other' && (
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value.slice(0, 600))}
                placeholder="Tell us more (optional)…"
                rows={3}
                className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 resize-none"
                data-testid="exit-survey-other-text"
              />
            )}

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleClose}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="exit-survey-cancel"
              >
                Not now
              </button>
              <button
                onClick={handleSubmit}
                disabled={!reason || submitting}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="exit-survey-submit"
              >
                <Send className="w-3.5 h-3.5" />
                {submitting ? 'Sending…' : 'Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitIntentSurvey;
